import type { ReactNode } from 'react';
import type { Overview } from './landing-facts';

/**
 * How a posting reaches you — written ONCE, rendered at two depths.
 *
 * The summary on the landing and the page at /how-it-works are the same four gates: the rail states
 * each in a sentence, the page gives it a paragraph. They are one list here rather than two because
 * the alternative is a marketing page and an explanation page that disagree about what the product
 * does, and nothing would catch it — both would read fine on their own.
 *
 * THE DEVICE IS THE PRODUCT'S. A tender is a chain of dated gates and `<GateStrip>` is how the
 * search screen draws one; the pipeline is also a sequence, so it is drawn the same way. That makes
 * the ordinals information — no gate can happen before the one before it — rather than four
 * paragraphs with numbers stuck on them.
 *
 * EVERY FIGURE IS STILL FETCHED. Three of these want a number or a name, and each is written so the
 * sentence survives without one: a shorter claim, never a placeholder and never a zero. Same rule as
 * the rest of the page — see landing-facts.ts.
 */

/** An LTR-isolated numeral island, so digits sit correctly inside Hebrew. */
function Num({ children }: { children: ReactNode }) {
  return <span className="huaca-num">{children}</span>;
}

export interface Gate {
  /** The one word the rail shows. */
  name: string;
  /** The heading the full page gives it. Carries no figure, so it needs nothing fetched. */
  title: string;
  /** One sentence, for the rail on the landing. */
  short: (overview: Overview | null) => ReactNode;
  /** The paragraph, for /how-it-works. */
  long: (overview: Overview | null) => ReactNode;
  /** An optional fetched footnote under the paragraph — absent when the fetch was. */
  note?: (overview: Overview | null) => ReactNode;
}

export const GATES: Gate[] = [
  {
    name: 'נסרק',
    title: 'נסרק — כל מקור, כל בוקר',
    short: () => 'כל מקור, כל בוקר. מה שהתפרסם אתמול בלילה נמצא בלוח כשאתה קם.',
    long: () =>
      'כל מקור שאנחנו מכסים נסרק כל בוקר, בלי קשר לכמה פרסומים יצאו ממנו. מה שהתפרסם אתמול בלילה ' +
      'נמצא בלוח כשאתה קם, ומקור שנוסף מגיע לכל המנויים באותו יום — לא בגרסה הבאה.',
    note: (o) => (o?.sources?.length ? `היום בלוח: ${o.sources.map((s) => s.publisher).join(', ')}.` : null),
  },
  {
    name: 'מפורק',
    title: 'מפורק — ממסמך לשדות',
    short: (o) =>
      o ? (
        <>
          כל פרסום נחתך ל-<Num>{o.fields}</Num> שדות, לפני שראית אותו ובלי לפתוח מסמך.
        </>
      ) : (
        'כל פרסום נחתך לשדות, לפני שראית אותו ובלי לפתוח מסמך.'
      ),
    /* The count is spliced INSIDE the phrase, never appended to it: "את השדות" and "את 15 השדות"
       are both grammatical, where a number dropped in beside a finished sentence is not. */
    long: (o) => (
      <>
        פרסום מגיע כמסמך. אנחנו מוציאים ממנו את {o ? <Num>{o.fields}</Num> : null}
        {o ? ' ' : ''}השדות שאתה בעצם מחפש — גוף מפרסם, תחום, אזור, סוג, מועדים ותנאי סף — לפני שראית
        אותו. זאת העבודה שאף אחד לא מוכן לעשות ביד, וזאת הסיבה שהלוח נקרא בעין ולא בפתיחת חוברות.
      </>
    ),
  },
  {
    name: 'מסונן',
    title: 'מסונן — לפי מה שרלוונטי לך',
    short: (o) =>
      o ? (
        <>
          <Num>{o.filters}</Num> מסננים, בחירה מרובה בכל אחד, ומיון לפי כל עמודה.
        </>
      ) : (
        'מסננים עם בחירה מרובה בכל אחד, ומיון לפי כל עמודה.'
      ),
    long: (o) => (
      <>
        {o ? <Num>{o.filters}</Num> : null}
        {o ? ' ' : ''}מסננים עם בחירה מרובה בכל אחד, מיון לפי כל עמודה, ואותה רשימה גם ככרטיסים אם ככה
        נוח לך לקרוא. הסינון חי בכתובת עצמה — אפשר לשמור חיפוש, לשלוח אותו למישהו, ולחזור אליו אחרי
        רענון.
      </>
    ),
  },
  {
    name: 'נפרש כרצף',
    title: 'נפרש כרצף — לא תאריך אחד',
    short: () => 'מפגש מציעים, שאלות הבהרה, הגשה — כל המועדים על ציר אחד.',
    long: () =>
      'מכרז הוא שרשרת מועדים: מפגש מציעים, שאלות הבהרה, הגשה. אנחנו פורשים את כולם על ציר אחד ' +
      'וצובעים לפי כמה קרוב השער הבא — כדי שתראה מה בוער היום, ולא רק מה נסגר בסוף.',
  },
];

/**
 * The rail, as it appears on the landing.
 *
 * An `<ol>`, because it is one. The ordinal is rendered rather than left to a list marker so it can
 * be labelled — "שער 1" reads as a stage, "1." reads as a bullet.
 */
export function GateRail({ overview }: { overview: Overview | null }) {
  return (
    <ol className="tenders-pipeline__rail">
      {GATES.map((gate, i) => (
        <li key={gate.name} className="tenders-pipeline__gate">
          <p className="tenders-pipeline__step">
            שער <Num>{i + 1}</Num>
          </p>
          <p className="tenders-pipeline__name">{gate.name}</p>
          <p className="tenders-pipeline__body">{gate.short(overview)}</p>
        </li>
      ))}
    </ol>
  );
}

/** The same four gates at full length — the body of /how-it-works. */
export function GateChapters({ overview }: { overview: Overview | null }) {
  return (
    <ol className="tenders-chapters">
      {GATES.map((gate, i) => {
        const note = gate.note?.(overview);
        return (
          <li key={gate.name} className="tenders-chapter">
            <p className="tenders-chapter__step">
              שער <Num>{i + 1}</Num>
            </p>
            <div>
              <h2>{gate.title}</h2>
              <p className="tenders-chapter__body">{gate.long(overview)}</p>
              {note ? <p className="tenders-chapter__note">{note}</p> : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
